'use client'

import type { Statistics } from '@/types/statistics'

import {
  BondType,
  type MunicipalType,
} from '@moment/trading-types/reference-data'
import React, { useMemo } from 'react'

import SegmentChart from '@/components/atomic/segment-chart'
import { useReferenceDataPermissions } from '@/hooks/use-reference-data-permissions'
import { referenceDataTransformationConfig } from '@/utils/presentation/instrument-data-transforms'

import {
  convertDistributionData,
  formatCouponLabel,
  getChartConfig,
} from './helpers'
import { formatMaturityBucketLabel } from './maturity-bucket-helpers'
import {
  DistributionsCard,
  DistributionsContainer,
  DistributionsGrid,
  DistributionsHeader,
  DistributionsText,
} from './styles'

interface DistributionData
  extends Pick<
    Statistics,
    | 'maturity_distribution'
    | 'sector_distribution'
    | 'sp_rating_distribution'
    | 'issuer_distribution'
    | 'coupon_distribution'
    | 'moodys_rating_distribution'
    | 'municipal_state_distribution'
    | 'municipal_type_distribution'
  > {}

interface DistributionsProps {
  distributionData: DistributionData
  total: number
  assetClassSet: Set<BondType>
  gridClassName?: string
  showHeader?: boolean
  useMaturityBuckets?: boolean
  isLoading?: boolean
}

export const Distributions = React.memo(function Distributions({
  distributionData,
  total,
  assetClassSet,
  gridClassName,
  showHeader = true,
  useMaturityBuckets = true,
  isLoading = false,
}: DistributionsProps) {
  const { hasSPAccess, hasMoodysAccess } = useReferenceDataPermissions()
  const titleCaseSectorDist = useMemo(() => {
    const titleCaseObj: Record<string, number> = {}
    Object.entries(distributionData.sector_distribution ?? {}).forEach(
      ([key, value]) => {
        if (value !== undefined) {
          // Convert key to title case with special handling for common words.
          const titleCaseKey = key
            .split(/[\s/]+/)
            .map((part) => {
              const lower = part.toLowerCase()
              if (
                lower === 'of' ||
                lower === 'and' ||
                lower === 'in' ||
                lower === 'the' ||
                lower === 'cash' ||
                lower === 'null'
              ) {
                return lower
              }
              return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
            })
            .join(key.includes('/') ? '/' : ' ')
            .trim()
          titleCaseObj[titleCaseKey] = (titleCaseObj[titleCaseKey] || 0) + value
        }
      },
    )
    return titleCaseObj
  }, [distributionData.sector_distribution])

  // Memoize all chart data
  const maturityData = useMemo(
    () =>
      convertDistributionData(
        distributionData.maturity_distribution,
        total,
        useMaturityBuckets,
        true,
      ),
    [distributionData.maturity_distribution, total, useMaturityBuckets],
  )

  const couponData = useMemo(
    () => convertDistributionData(distributionData.coupon_distribution, total),
    [distributionData.coupon_distribution, total],
  )

  const issuerData = useMemo(
    () => convertDistributionData(distributionData.issuer_distribution, total),
    [distributionData.issuer_distribution, total],
  )

  const ratingData = useMemo(
    () =>
      convertDistributionData(distributionData.sp_rating_distribution, total),
    [distributionData.sp_rating_distribution, total],
  )

  const moodysRatingData = useMemo(
    () =>
      convertDistributionData(
        distributionData.moodys_rating_distribution,
        total,
      ),
    [distributionData.moodys_rating_distribution, total],
  )

  const sectorData = useMemo(
    () => convertDistributionData(titleCaseSectorDist, total),
    [titleCaseSectorDist, total],
  )

  const stateData = useMemo(
    () =>
      convertDistributionData(
        distributionData.municipal_state_distribution,
        total,
      ),
    [distributionData.municipal_state_distribution, total],
  )

  const typeData = useMemo(
    () =>
      convertDistributionData(
        distributionData.municipal_type_distribution,
        total,
      ),
    [distributionData.municipal_type_distribution, total],
  )

  // Memoize all chart configs
  const maturityChartConfig = useMemo(() => {
    // Use appropriate label formatter based on whether we're using buckets
    if (useMaturityBuckets) {
      return getChartConfig(maturityData, formatMaturityBucketLabel)
    }

    return getChartConfig(
      distributionData.maturity_distribution,
      (label: string) =>
        referenceDataTransformationConfig.maturity_date.valueTransformation(
          label,
        ),
    )
  }, [distributionData.maturity_distribution, maturityData, useMaturityBuckets])

  const couponChartConfig = useMemo(
    () =>
      getChartConfig(distributionData.coupon_distribution, (label: string) =>
        formatCouponLabel(label),
      ),
    [distributionData.coupon_distribution],
  )

  const issuerChartConfig = useMemo(
    () => getChartConfig(distributionData.issuer_distribution),
    [distributionData.issuer_distribution],
  )

  const ratingChartConfig = useMemo(
    () => getChartConfig(distributionData.sp_rating_distribution),
    [distributionData.sp_rating_distribution],
  )

  const moodysRatingChartConfig = useMemo(
    () => getChartConfig(distributionData.moodys_rating_distribution),
    [distributionData.moodys_rating_distribution],
  )

  const sectorChartConfig = useMemo(
    () => getChartConfig(titleCaseSectorDist),
    [titleCaseSectorDist],
  )

  const stateChartConfig = useMemo(
    () => getChartConfig(distributionData.municipal_state_distribution),
    [distributionData.municipal_state_distribution],
  )

  const typeChartConfig = useMemo(
    () =>
      getChartConfig(
        distributionData.municipal_type_distribution,
        (label: MunicipalType) =>
          referenceDataTransformationConfig.municipal_type.valueTransformation(
            label,
          ),
      ),
    [distributionData.municipal_type_distribution],
  )

  return (
    <DistributionsContainer>
      {showHeader && (
        <DistributionsHeader>
          <DistributionsText>Distribution</DistributionsText>
        </DistributionsHeader>
      )}
      <DistributionsGrid className={gridClassName}>
        <DistributionsCard className="mt-0">
          <SegmentChart
            chartData={maturityData}
            chartConfig={maturityChartConfig}
            title="Maturity distribution"
            isLoading={isLoading}
            fullWidth
          />
        </DistributionsCard>

        <DistributionsCard className="mt-0">
          <SegmentChart
            chartData={couponData}
            chartConfig={couponChartConfig}
            title="Coupon distribution"
            isLoading={isLoading}
            fullWidth
          />
        </DistributionsCard>

        {!assetClassSet.has(BondType.Treasury) && (
          <DistributionsCard className="mt-0">
            <SegmentChart
              chartData={issuerData}
              chartConfig={issuerChartConfig}
              title="Issuer distribution"
              isLoading={isLoading}
              fullWidth
            />
          </DistributionsCard>
        )}

        {!assetClassSet.has(BondType.Treasury) && hasSPAccess && (
          <DistributionsCard className="mt-0">
            <SegmentChart
              chartData={ratingData}
              chartConfig={ratingChartConfig}
              title="S&P rating distribution"
              isLoading={isLoading}
              fullWidth
            />
          </DistributionsCard>
        )}

        {!assetClassSet.has(BondType.Treasury) && hasMoodysAccess && (
          <DistributionsCard className="mt-0">
            <SegmentChart
              chartData={moodysRatingData}
              chartConfig={moodysRatingChartConfig}
              title="Moody's rating distribution"
              isLoading={isLoading}
              fullWidth
            />
          </DistributionsCard>
        )}

        {assetClassSet.has(BondType.Corporate) && (
          <DistributionsCard className="mt-0">
            <SegmentChart
              chartData={sectorData}
              chartConfig={sectorChartConfig}
              title="Sector distribution"
              isLoading={isLoading}
              fullWidth
            />
          </DistributionsCard>
        )}

        {assetClassSet.has(BondType.Municipal) && (
          <DistributionsCard className="mt-0">
            <SegmentChart
              chartData={stateData}
              chartConfig={stateChartConfig}
              title="Municipal state distribution"
              isLoading={isLoading}
              fullWidth
            />
          </DistributionsCard>
        )}

        {assetClassSet.has(BondType.Municipal) && (
          <DistributionsCard>
            <SegmentChart
              chartData={typeData}
              chartConfig={typeChartConfig}
              title="Municipal type distribution"
              isLoading={isLoading}
              fullWidth
            />
          </DistributionsCard>
        )}
      </DistributionsGrid>
    </DistributionsContainer>
  )
})
